import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, GripVertical, ChevronUp, ChevronDown, Plus, X, Save, Loader2, Home } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import axios from 'axios';
import { sortFeatured, HOME_FEATURED_COUNT } from '@/lib/featured';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Admin > Featured Order. Choose which paintings are featured and drag them into the order you like.
 * The first HOME_FEATURED_COUNT appear on the homepage. Nothing changes on the site until "Save".
 * Dragging uses native HTML5 drag and drop (mouse); the arrow buttons work everywhere, including touch screens.
 */
const FeaturedOrder = () => {
  const [artworks, setArtworks] = useState([]);       // every artwork (from the API)
  const [ids, setIds] = useState([]);                  // featured artwork ids, in display order (working copy)
  const [savedIds, setSavedIds] = useState([]);        // last saved order
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState('');
  const [dragIndex, setDragIndex] = useState(null);
  const [overIndex, setOverIndex] = useState(null);

  const token = localStorage.getItem('admin_token');

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/artworks`);
      setArtworks(res.data);
      const order = sortFeatured(res.data).map((a) => a.id);
      setIds(order);
      setSavedIds(order);
    } catch {
      toast.error('Failed to load artworks');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const byId = useMemo(() => Object.fromEntries(artworks.map((a) => [a.id, a])), [artworks]);
  const dirty = ids.length !== savedIds.length || ids.some((id, i) => id !== savedIds[i]);

  // Warn before leaving with unsaved changes
  useEffect(() => {
    if (!dirty) return undefined;
    const warn = (e) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [dirty]);

  const move = (from, to) => {
    if (to < 0 || to >= ids.length || from === to) return;
    setIds((prev) => {
      const next = prev.slice();
      const [item] = next.splice(from, 1);
      next.splice(to, 0, item);
      return next;
    });
  };

  const remove = (id) => setIds((prev) => prev.filter((x) => x !== id));
  const add = (id) => setIds((prev) => (prev.includes(id) ? prev : [...prev, id]));

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/featured-artworks`, { ids }, { headers: { Authorization: `Bearer ${token}` } });
      setSavedIds(ids);
      toast.success('Featured order saved. The homepage is updated.');
      await load();
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Could not save');
    } finally {
      setSaving(false);
    }
  };

  const candidates = useMemo(() => {
    const q = search.trim().toLowerCase();
    return artworks
      .filter((a) => !ids.includes(a.id))
      .filter((a) => !q || (a.title || '').toLowerCase().includes(q) || (a.category || '').toLowerCase().includes(q));
  }, [artworks, ids, search]);

  if (loading) {
    return <div className="min-h-screen pt-28 flex items-center justify-center"><Loader2 className="animate-spin" /></div>;
  }

  return (
    <div className="min-h-screen pt-20 pb-24 px-4 sm:px-6 md:px-12" data-testid="featured-order-page">
      <div className="max-w-screen-lg mx-auto">
        <div className="flex items-center justify-between gap-3 mb-2 flex-wrap">
          <div className="flex items-center gap-3">
            <Link to="/admin/dashboard" className="text-muted-foreground hover:text-foreground"><ArrowLeft size={20} /></Link>
            <h1 className="text-2xl font-serif font-light">Featured Order</h1>
          </div>
          <div className="flex items-center gap-3">
            {dirty && <span className="text-xs text-amber-700" data-testid="unsaved-note">Unsaved changes</span>}
            <Button onClick={save} disabled={!dirty || saving} data-testid="save-featured-order">
              {saving ? <Loader2 size={14} className="mr-2 animate-spin" /> : <Save size={14} className="mr-2" />}
              Save order
            </Button>
          </div>
        </div>
        <p className="text-sm text-muted-foreground mb-6">
          Drag paintings (or use the arrows) into the order you like. The first {HOME_FEATURED_COUNT} are shown on the
          homepage. Add more from the list below. Nothing changes on the site until you press <strong>Save order</strong>.
        </p>

        {ids.length === 0 ? (
          <p className="text-sm text-muted-foreground border border-dashed border-border rounded-lg p-6 text-center">
            No featured paintings yet. Add some from the list below.
          </p>
        ) : (
          <ol className="space-y-2" data-testid="featured-list">
            {ids.map((id, index) => {
              const a = byId[id];
              if (!a) return null;
              const onHome = index < HOME_FEATURED_COUNT;
              return (
                <React.Fragment key={id}>
                  {index === HOME_FEATURED_COUNT && (
                    <li aria-hidden className="text-[11px] uppercase tracking-[0.15em] text-muted-foreground py-1 text-center border-t border-dashed border-border" data-testid="homepage-cutoff">
                      Not shown on the homepage
                    </li>
                  )}
                  <li
                    draggable
                    onDragStart={(e) => { setDragIndex(index); e.dataTransfer.effectAllowed = 'move'; e.dataTransfer.setData('text/plain', id); }}
                    onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; if (overIndex !== index) setOverIndex(index); }}
                    onDrop={(e) => { e.preventDefault(); if (dragIndex !== null) move(dragIndex, index); setDragIndex(null); setOverIndex(null); }}
                    onDragEnd={() => { setDragIndex(null); setOverIndex(null); }}
                    className={`flex items-center gap-3 p-2 rounded-lg border bg-card transition-colors ${
                      overIndex === index && dragIndex !== null && dragIndex !== index ? 'border-accent ring-1 ring-accent' : 'border-border'
                    } ${dragIndex === index ? 'opacity-40' : ''}`}
                    data-testid={`featured-item-${index}`}
                  >
                    <span className="cursor-grab text-muted-foreground touch-none" title="Drag to reorder"><GripVertical size={18} /></span>
                    <span className="w-6 text-center text-sm font-medium text-muted-foreground">{index + 1}</span>
                    <img src={a.thumb_url || a.image_url} alt="" draggable={false} className="w-14 h-14 object-cover rounded" />
                    <div className="flex-1 min-w-0">
                      <p className="font-serif text-sm sm:text-base truncate">{a.title}</p>
                      <p className="text-xs text-muted-foreground truncate">{a.category}</p>
                    </div>
                    {onHome && (
                      <span className="hidden sm:inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full bg-green-100 text-green-700 font-medium">
                        <Home size={10} /> Homepage
                      </span>
                    )}
                    <div className="flex items-center">
                      <button type="button" onClick={() => move(index, index - 1)} disabled={index === 0} aria-label="Move up"
                        className="p-1.5 text-muted-foreground hover:text-foreground disabled:opacity-30" data-testid={`move-up-${index}`}><ChevronUp size={18} /></button>
                      <button type="button" onClick={() => move(index, index + 1)} disabled={index === ids.length - 1} aria-label="Move down"
                        className="p-1.5 text-muted-foreground hover:text-foreground disabled:opacity-30" data-testid={`move-down-${index}`}><ChevronDown size={18} /></button>
                      <button type="button" onClick={() => remove(id)} aria-label="Remove from featured" title="Remove from featured"
                        className="p-1.5 text-muted-foreground hover:text-red-600" data-testid={`remove-${index}`}><X size={18} /></button>
                    </div>
                  </li>
                </React.Fragment>
              );
            })}
          </ol>
        )}

        <h2 className="font-sans text-xs uppercase tracking-[0.15em] text-muted-foreground mt-10 mb-3">Add to featured</h2>
        <Input placeholder="Search by title or category" value={search} onChange={(e) => setSearch(e.target.value)} className="mb-4 max-w-sm" data-testid="featured-search" />
        {candidates.length === 0 ? (
          <p className="text-sm text-muted-foreground">No other paintings{search ? ' match your search' : ''}.</p>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3" data-testid="featured-candidates">
            {candidates.map((a) => (
              <button key={a.id} type="button" onClick={() => add(a.id)}
                className="group text-left rounded-lg border border-border bg-card overflow-hidden hover:border-accent transition-colors"
                data-testid={`add-${a.id}`}>
                <div className="relative aspect-square bg-muted">
                  <img src={a.thumb_url || a.image_url} alt="" draggable={false} loading="lazy" className="w-full h-full object-cover" />
                  <span className="absolute top-2 right-2 inline-flex items-center justify-center w-7 h-7 rounded-full bg-accent text-white opacity-90 group-hover:opacity-100"><Plus size={16} /></span>
                </div>
                <p className="p-2 text-xs truncate">{a.title}</p>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default FeaturedOrder;
